import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Select from 'react-select';
import './client-dashboard.css';

const ClientDashboardView = ({
  dashboardData,
  onFilterChange,
  onBack,
  onRefresh,
  queryParams,
}) => {
  const [filters, setFilters] = useState({
    startDate: queryParams.startDate || '',
    endDate: queryParams.endDate || '',
    employee_id: queryParams.employee_id || '',
    status_filter: queryParams.status_filter || '',
    page: queryParams.page || 1,
    limit: queryParams.limit || 10,
    include_activities: 'true',
    include_timeline: 'true',
  });

  // Helper function to format dates for chips
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Active view (restore tabs)
  const [activeView, setActiveView] = useState('timeline');

  const handleSetView = (v) => setActiveView(v);

  // Force activities & timeline included
  useEffect(() => {
    if (
      filters.include_activities !== 'true' ||
      filters.include_timeline !== 'true'
    ) {
      const fixed = {
        ...filters,
        include_activities: 'true',
        include_timeline: 'true',
      };
      setFilters(fixed);
      onFilterChange(fixed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter updates
  const updateFilter = (key, value) => {
    // When changing filters other than page, reset to page 1.
    // When changing page itself, keep the requested page.
    let newFilters;
    if (key === 'page') {
      newFilters = { ...filters, page: value };
    } else {
      newFilters = { ...filters, [key]: value, page: 1 };
    }
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  // Format date for display
  const formatDateTime = (date) => {
    return moment(date).format('MMM DD, YYYY HH:mm');
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusColors = {
      PENDING: '#ffa500',
      IN_PROGRESS: '#2196f3',
      COMPLETED: '#4caf50',
      ON_HOLD: '#ff5722',
      CANCELLED: '#f44336',
    };
    const color = statusColors[status?.toUpperCase()] || '#757575';
    return (
      <span className='status-badge' style={{ backgroundColor: color }}>
        {status}
      </span>
    );
  };

  // Summary & status breakdown
  const summary = dashboardData?.summary || {};
  const statusBreakdown = dashboardData?.status_breakdown || [];
  const getFromBreakdown = (arr) => {
    const found = statusBreakdown.find((s) =>
      arr.includes((s.name || '').toLowerCase())
    );
    return found?.count || 0;
  };
  const totalActivities = summary.total_activities || 0;
  const completedActivities = summary.completed_activities || 0;
  const overdueActivities = summary.overdue_activities || 0;
  let pendingActivities = summary.pending_activities ?? summary.pending ?? 0;
  if (!pendingActivities) pendingActivities = getFromBreakdown(['pending']);
  let onHoldActivities =
    summary.on_hold_activities ??
    summary.onhold_activities ??
    summary.on_hold ??
    0;
  if (!onHoldActivities)
    onHoldActivities = getFromBreakdown(['on hold', 'on_hold', 'onhold']);
  const completionRate = summary.completion_rate || 0;

  // Timeline reconstruction
  const rawTimeline = Array.isArray(dashboardData?.activity_timeline)
    ? [...dashboardData.activity_timeline]
    : [];
  rawTimeline.sort((a, b) => (a._id > b._id ? 1 : -1));
  const rangeStart = filters.startDate
    ? moment(filters.startDate)
    : moment().subtract(29, 'day');
  const rangeEnd = filters.endDate ? moment(filters.endDate) : moment();
  const timelineMap = rawTimeline.reduce((acc, d) => {
    acc[d._id] = d.count;
    return acc;
  }, {});
  const filled = [];
  const cursor = rangeStart.clone();
  while (cursor.isSameOrBefore(rangeEnd, 'day')) {
    const key = cursor.format('YYYY-MM-DD');
    filled.push({ _id: key, count: timelineMap[key] || 0 });
    cursor.add(1, 'day');
  }
  const maxTimelineCount = filled.reduce(
    (m, d) => (d.count > m ? d.count : m),
    0
  );
  const totalTimelineCount = filled.reduce((s, d) => s + d.count, 0);
  const enrichedTimelineFull = filled.map((d, idx) => {
    const cumulative = filled
      .slice(0, idx + 1)
      .reduce((s, x) => s + x.count, 0);
    return {
      ...d,
      date: d._id,
      weekday: moment(d._id).format('ddd'),
      pct: totalTimelineCount
        ? ((d.count / totalTimelineCount) * 100).toFixed(1)
        : '0.0',
      cumulative,
    };
  });
  const [timelinePage, setTimelinePage] = useState(1);
  const timelinePageSize = 30;
  const totalTimelinePages = Math.max(
    1,
    Math.ceil(enrichedTimelineFull.length / timelinePageSize)
  );
  const clampedTimelinePage = Math.min(timelinePage, totalTimelinePages);
  useEffect(() => {
    setTimelinePage(1);
  }, [filters.startDate, filters.endDate]);
  const paginatedTimeline = enrichedTimelineFull.slice(
    (clampedTimelinePage - 1) * timelinePageSize,
    clampedTimelinePage * timelinePageSize
  );
  const [timelineMode, setTimelineMode] = useState('overview');
  const [barTooltip, setBarTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });
  const hideTooltip = () => setBarTooltip((t) => ({ ...t, visible: false }));
  const handleBarClick = (dateStr) => applyRange(dateStr, dateStr);
  const statusTotal = statusBreakdown.reduce((s, x) => s + x.count, 0);
  const statusPieData = statusBreakdown.map((s) => ({
    name: s.name,
    value: s.count,
    pct: statusTotal ? ((s.count / statusTotal) * 100).toFixed(1) : '0.0',
    color: s.color || '#6b7280',
  }));
  // Pie helpers (arc-based instead of dashed circles for accurate hover)
  const pieRadius = 54;
  const [statusPieTooltip, setStatusPieTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });
  const hideStatusPieTooltip = () =>
    setStatusPieTooltip((t) => ({ ...t, visible: false }));

  // Process breakdown pie
  const processBreakdown = dashboardData?.process_breakdown || [];
  const processTotal = processBreakdown.reduce((s, x) => s + (x.count || 0), 0);
  const processPalette = [
    '#6366f1',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#3b82f6',
    '#8b5cf6',
    '#ec4899',
    '#14b8a6',
    '#f97316',
    '#0ea5e9',
  ];
  const processPieData = processBreakdown.map((p, i) => ({
    name: p.name,
    value: p.count,
    pct: processTotal ? ((p.count / processTotal) * 100).toFixed(1) : '0.0',
    color: processPalette[i % processPalette.length],
  }));
  const [processPieTooltip, setProcessPieTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });
  const hideProcessPieTooltip = () =>
    setProcessPieTooltip((t) => ({ ...t, visible: false }));

  // Utility to build arc path (start/end angles in degrees, 0 at top, clockwise)
  const buildArcPath = (startAngleDeg, endAngleDeg, r) => {
    // Convert to conventional circle (0 deg at right) by offsetting -90 deg
    const toRad = (deg) => (Math.PI / 180) * (deg - 90);
    const startRad = toRad(startAngleDeg);
    const endRad = toRad(endAngleDeg);
    const sx = r * Math.cos(startRad);
    const sy = r * Math.sin(startRad);
    const ex = r * Math.cos(endRad);
    const ey = r * Math.sin(endRad);
    const largeArc = endAngleDeg - startAngleDeg > 180 ? 1 : 0;
    return `M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`;
  };

  const renderPieArcs = (data, total, setTooltip, hideTooltip) => {
    let cumulative = 0;
    return data
      .filter((seg) => seg.value > 0)
      .map((seg, i) => {
        const fraction = seg.value / total;
        const startAngle = cumulative * 360 - 90; // we rotate later so initial -90 handled inside path util
        cumulative += fraction;
        const endAngle = cumulative * 360 - 90;
        // Special full-circle handling
        if (fraction >= 0.999) {
          return (
            <circle
              key={seg.name + i}
              r={pieRadius}
              cx='0'
              cy='0'
              fill='transparent'
              stroke={seg.color}
              strokeWidth='28'
              onMouseEnter={(e) => {
                const b = e.currentTarget.getBoundingClientRect();
                setTooltip({
                  visible: true,
                  x: b.left + b.width / 2,
                  y: b.top - 8,
                  content: (
                    <div className='tt-inner'>
                      <div className='tt-date'>{seg.name}</div>
                      <div className='tt-total'>
                        <strong>{seg.value}</strong> activities ({seg.pct}%)
                      </div>
                    </div>
                  ),
                });
              }}
              onMouseMove={(e) => {
                const b = e.currentTarget.getBoundingClientRect();
                setTooltip((t) => ({
                  ...t,
                  x: b.left + b.width / 2,
                  y: b.top - 8,
                }));
              }}
              onMouseLeave={hideTooltip}
            />
          );
        }
        const d = buildArcPath(startAngle + 90, endAngle + 90, pieRadius); // adjust since buildArcPath expects startAngle from top
        return (
          <path
            key={seg.name + i}
            d={d}
            fill='none'
            stroke={seg.color}
            strokeWidth='28'
            strokeLinecap='butt'
            onMouseEnter={(e) => {
              const b = e.currentTarget.getBoundingClientRect();
              setTooltip({
                visible: true,
                x: b.left + b.width / 2,
                y: b.top - 8,
                content: (
                  <div className='tt-inner'>
                    <div className='tt-date'>{seg.name}</div>
                    <div className='tt-total'>
                      <strong>{seg.value}</strong> activities ({seg.pct}%)
                    </div>
                  </div>
                ),
              });
            }}
            onMouseMove={(e) => {
              const b = e.currentTarget.getBoundingClientRect();
              setTooltip((t) => ({
                ...t,
                x: b.left + b.width / 2,
                y: b.top - 8,
              }));
            }}
            onMouseLeave={hideTooltip}
          />
        );
      });
  };

  // Team interactive state
  const [teamSort, setTeamSort] = useState({ key: 'total_tasks', dir: 'desc' });
  const [teamSearch, setTeamSearch] = useState('');
  const cycleSort = (key) => {
    setTeamSort((prev) => {
      if (prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return { key: 'total_tasks', dir: 'desc' };
    });
  };
  const teamDataRaw = Array.isArray(dashboardData?.employee_workload)
    ? dashboardData.employee_workload
    : [];
  const teamFiltered = teamDataRaw.filter(
    (emp) =>
      !teamSearch ||
      (emp.employee?.name || '')
        .toLowerCase()
        .includes(teamSearch.toLowerCase())
  );
  const teamSorted = [...teamFiltered].sort((a, b) => {
    const { key, dir } = teamSort;
    const av = a[key] ?? 0;
    const bv = b[key] ?? 0;
    if (av === bv) return 0;
    return dir === 'asc' ? (av < bv ? -1 : 1) : av > bv ? -1 : 1;
  });
  const sortIcon = (key) => {
    if (teamSort.key !== key) return '↕';
    return teamSort.dir === 'desc' ? '↓' : '↑';
  };

  // Employee & Status filter options
  const employeeOptions =
    dashboardData?.filters?.available?.employees?.map((e) => ({
      value: e._id,
      label: e.name,
    })) || [];
  const statusOptions =
    dashboardData?.filters?.available?.statuses?.map((s) => ({
      value: s._id,
      label: s.name,
    })) || [];

  // Quick date ranges
  const fmt = (m) => m.format('YYYY-MM-DD');
  const today = fmt(moment());
  const yesterday = fmt(moment().subtract(1, 'day'));
  const last7Start = fmt(moment().subtract(6, 'day'));
  const last30Start = fmt(moment().subtract(29, 'day'));
  const thisMonthStart = fmt(moment().startOf('month'));
  const lastMonthStart = fmt(moment().subtract(1, 'month').startOf('month'));
  const lastMonthEnd = fmt(moment().subtract(1, 'month').endOf('month'));
  const applyRange = (start, end) => {
    const newFilters = { ...filters, startDate: start, endDate: end, page: 1 };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };
  const clearDates = () => applyRange('', '');
  const quickRanges = [
    { key: 'today', label: 'Today', start: today, end: today },
    { key: 'yesterday', label: 'Yesterday', start: yesterday, end: yesterday },
    { key: 'last7', label: 'Last 7 Days', start: last7Start, end: today },
    { key: 'last30', label: 'Last 30 Days', start: last30Start, end: today },
    {
      key: 'thisMonth',
      label: 'This Month',
      start: thisMonthStart,
      end: today,
    },
    {
      key: 'lastMonth',
      label: 'Last Month',
      start: lastMonthStart,
      end: lastMonthEnd,
    },
  ];
  const isActiveRange = (r) =>
    filters.startDate === r.start && filters.endDate === r.end;

  // Pagination helpers (activities)
  const rawPagination = dashboardData?.pagination;
  const paginationLimit = parseInt(
    rawPagination?.limit || filters.limit || 10,
    10
  );
  const currentPage = parseInt(
    rawPagination?.current_page || filters.page || 1,
    10
  );
  const totalActivityCount = parseInt(
    rawPagination?.total_activities ?? summary.total_activities ?? 0,
    10
  );
  const totalPages =
    totalActivityCount > 0
      ? Math.ceil(totalActivityCount / paginationLimit)
      : 1;
  const pageStartIndex =
    totalActivityCount === 0 ? 0 : (currentPage - 1) * paginationLimit + 1;
  const pageEndIndex = Math.min(
    currentPage * paginationLimit,
    totalActivityCount
  );
  const gotoPage = (p) => {
    const target = Math.min(Math.max(1, p), totalPages);
    if (target !== filters.page) updateFilter('page', target);
  };
  const buildPageItems = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    const add = (p) => pages.push(p);
    const addEllipsis = () => pages.push('ellipsis-' + pages.length);
    add(1);
    const leftWindow = Math.max(2, currentPage - 1);
    const rightWindow = Math.min(totalPages - 1, currentPage + 1);
    if (leftWindow > 2) addEllipsis();
    for (let p = leftWindow; p <= rightWindow; p++) add(p);
    if (rightWindow < totalPages - 1) addEllipsis();
    add(totalPages);
    return pages;
  };
  const smartPages = buildPageItems();

  return (
    <div className='client-dashboard-container'>
      {/* Compact Filters Section */}
      <div className='dashboard-filters-compact'>
        {/* Filter Controls Row */}
        <div className='filters-row'>
          <div className='filter-group-compact'>
            <input
              type='date'
              value={filters.startDate}
              onChange={(e) => updateFilter('startDate', e.target.value)}
              className='filter-input-compact'
              placeholder='Start Date'
              title='Start Date'
            />
          </div>

          <div className='filter-group-compact'>
            <input
              type='date'
              value={filters.endDate}
              onChange={(e) => updateFilter('endDate', e.target.value)}
              className='filter-input-compact'
              placeholder='End Date'
              title='End Date'
            />
          </div>

          <div className='filter-group-compact'>
            <Select
              options={employeeOptions}
              value={employeeOptions.find(
                (opt) => opt.value === filters.employee_id
              )}
              onChange={(option) =>
                updateFilter('employee_id', option?.value || '')
              }
              placeholder='Select Employee'
              isClearable
              className='filter-select-compact'
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '36px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '6px',
                  fontSize: '14px',
                }),
                placeholder: (base) => ({
                  ...base,
                  fontSize: '14px',
                }),
              }}
            />
          </div>

          <div className='filter-group-compact'>
            <Select
              options={statusOptions}
              value={statusOptions.find(
                (opt) => opt.value === filters.status_filter
              )}
              onChange={(option) =>
                updateFilter('status_filter', option?.value || '')
              }
              placeholder='Select Status'
              isClearable
              className='filter-select-compact'
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '36px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '6px',
                  fontSize: '14px',
                }),
                placeholder: (base) => ({
                  ...base,
                  fontSize: '14px',
                }),
              }}
            />
          </div>

          {/* Removed toggles: activities & timeline always included now */}
        </div>

        {/* Active Filters Chips */}
        {(filters.startDate ||
          filters.endDate ||
          filters.employee_id ||
          filters.status_filter) && (
          <div className='active-filters-chips'>
            <span className='chips-label'>Filters:</span>
            {filters.startDate && (
              <div className='filter-chip'>
                <span>{formatDate(filters.startDate)}</span>
                <button
                  className='chip-remove'
                  onClick={() => updateFilter('startDate', '')}
                  title='Remove start date filter'
                >
                  ×
                </button>
              </div>
            )}
            {filters.endDate && (
              <div className='filter-chip'>
                <span>{formatDate(filters.endDate)}</span>
                <button
                  className='chip-remove'
                  onClick={() => updateFilter('endDate', '')}
                  title='Remove end date filter'
                >
                  ×
                </button>
              </div>
            )}
            {filters.employee_id && (
              <div className='filter-chip'>
                <span>
                  {
                    employeeOptions.find(
                      (opt) => opt.value === filters.employee_id
                    )?.label
                  }
                </span>
                <button
                  className='chip-remove'
                  onClick={() => updateFilter('employee_id', '')}
                  title='Remove employee filter'
                >
                  ×
                </button>
              </div>
            )}
            {filters.status_filter && (
              <div className='filter-chip'>
                <span>
                  {
                    statusOptions.find(
                      (opt) => opt.value === filters.status_filter
                    )?.label
                  }
                </span>
                <button
                  className='chip-remove'
                  onClick={() => updateFilter('status_filter', '')}
                  title='Remove status filter'
                >
                  ×
                </button>
              </div>
            )}
            <button
              className='clear-all-filters'
              onClick={() => {
                setFilters({
                  ...filters,
                  startDate: '',
                  endDate: '',
                  employee_id: '',
                  status_filter: '',
                  page: 1,
                });
                onFilterChange({
                  ...filters,
                  startDate: '',
                  endDate: '',
                  employee_id: '',
                  status_filter: '',
                  page: 1,
                });
              }}
              title='Clear all filters'
            >
              <i className='fa fa-times'></i> Clear
            </button>
          </div>
        )}

        {/* Quick Date Ranges */}
        <div className='quick-date-filters'>
          {quickRanges.map((r) => (
            <button
              key={r.key}
              type='button'
              className={`quick-range-btn ${isActiveRange(r) ? 'active' : ''}`}
              onClick={() => applyRange(r.start, r.end)}
              title={`${r.label}: ${r.start} to ${r.end}`}
            >
              {r.label}
            </button>
          ))}
          <button
            type='button'
            className={`quick-range-btn clear ${
              !filters.startDate && !filters.endDate ? 'active' : ''
            }`}
            onClick={clearDates}
            title='Clear date range'
          >
            None
          </button>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className='compact-metrics'>
        <div className='metric-card'>
          <span className='metric-icon bg-primary-soft'>
            <i className='fa fa-tasks'></i>
          </span>
          <div className='metric-data'>
            <span className='metric-value'>{totalActivities}</span>
            <span className='metric-label'>Total</span>
          </div>
        </div>
        <div className='metric-card'>
          <span className='metric-icon bg-warning'>
            <i className='fa fa-hourglass-half'></i>
          </span>
          <div className='metric-data'>
            <span className='metric-value'>{pendingActivities}</span>
            <span className='metric-label'>Pending</span>
          </div>
        </div>
        <div className='metric-card'>
          <span className='metric-icon bg-slate'>
            <i className='fa fa-pause-circle'></i>
          </span>
          <div className='metric-data'>
            <span className='metric-value'>{onHoldActivities}</span>
            <span className='metric-label'>On Hold</span>
          </div>
        </div>
        <div className='metric-card'>
          <span className='metric-icon bg-success'>
            <i className='fa fa-check-circle'></i>
          </span>
          <div className='metric-data'>
            <span className='metric-value'>{completedActivities}</span>
            <span className='metric-label'>Completed</span>
          </div>
        </div>
        <div className='metric-card'>
          <span className='metric-icon bg-danger'>
            <i className='fa fa-exclamation-triangle'></i>
          </span>
          <div className='metric-data'>
            <span className='metric-value'>{overdueActivities}</span>
            <span className='metric-label'>Overdue</span>
          </div>
        </div>
        <div className='metric-card'>
          <span className='metric-icon bg-info'>
            <i className='fa fa-chart-pie'></i>
          </span>
          <div className='metric-data'>
            <span className='metric-value'>{completionRate}%</span>
            <span className='metric-label'>Completion</span>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className='view-tabs'>
        <button
          className={`tab-btn ${activeView === 'timeline' ? 'active' : ''}`}
          onClick={() => handleSetView('timeline')}
        >
          Timeline
        </button>
        <button
          className={`tab-btn ${activeView === 'activities' ? 'active' : ''}`}
          onClick={() => handleSetView('activities')}
        >
          Activities
        </button>

        <button
          className={`tab-btn ${activeView === 'employees' ? 'active' : ''}`}
          onClick={() => handleSetView('employees')}
        >
          Team Performance
        </button>
      </div>

      <div className='dashboard-content'>
        {activeView === 'activities' && (
          <div className='activities-view'>
            {dashboardData?.recent_activities?.length ? (
              <div className='activities-table'>
                <h3>Recent Activities</h3>
                <table className='table'>
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Assigned To</th>
                      <th>Status</th>
                      <th>Due Date</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recent_activities.map((activity, index) => (
                      <tr key={index}>
                        <td>{activity.task_name}</td>
                        <td>{activity.assigned_to?.name || 'Unassigned'}</td>
                        <td>
                          {getStatusBadge(activity.activity_status?.name)}
                        </td>
                        <td>{formatDateTime(activity.due_time)}</td>
                        <td>{formatDateTime(activity.updatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rawPagination && (
                  <div className='pagination-info new-pagination'>
                    <div className='pagination-summary'>
                      Showing {pageStartIndex}-{pageEndIndex} of{' '}
                      {totalActivityCount} | Page {currentPage} of {totalPages}
                    </div>
                    <div className='pagination-controls'>
                      <button
                        className='pg-btn'
                        disabled={currentPage === 1}
                        onClick={() => gotoPage(1)}
                      >
                        «
                      </button>
                      <button
                        className='pg-btn'
                        disabled={currentPage === 1}
                        onClick={() => gotoPage(currentPage - 1)}
                      >
                        ‹
                      </button>
                      {smartPages.map((p) =>
                        typeof p === 'number' ? (
                          <button
                            key={p}
                            className={`pg-btn number ${
                              p === currentPage ? 'active' : ''
                            }`}
                            onClick={() => gotoPage(p)}
                          >
                            {p}
                          </button>
                        ) : (
                          <span key={p} className='pg-ellipsis'>
                            …
                          </span>
                        )
                      )}
                      <button
                        className='pg-btn'
                        disabled={currentPage === totalPages}
                        onClick={() => gotoPage(currentPage + 1)}
                      >
                        ›
                      </button>
                      <button
                        className='pg-btn'
                        disabled={currentPage === totalPages}
                        onClick={() => gotoPage(totalPages)}
                      >
                        »
                      </button>
                      <div className='page-size-select-wrapper'>
                        <select
                          className='page-size-select'
                          value={paginationLimit}
                          onChange={(e) =>
                            updateFilter('limit', parseInt(e.target.value, 10))
                          }
                        >
                          {[10, 25, 50, 100].map((sz) => (
                            <option key={sz} value={sz}>
                              {sz}/page
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className='empty-state'>
                <i className='fa fa-tasks'></i>
                <h3>No Activities Found</h3>
                <p>No activities match your current filters.</p>
              </div>
            )}
          </div>
        )}

        {activeView === 'timeline' && (
          <div className='timeline-view'>
            {enrichedTimelineFull.length ? (
              <>
                <div className='timeline-header'>
                  <h3 className='timeline-title'>Activity Timeline</h3>
                  <div className='timeline-actions'>
                    <div className='mode-toggle'>
                      <button
                        type='button'
                        className={`mode-btn ${
                          timelineMode === 'overview' ? 'active' : ''
                        }`}
                        onClick={() => setTimelineMode('overview')}
                      >
                        Overview
                      </button>
                      <button
                        type='button'
                        className={`mode-btn ${
                          timelineMode === 'table' ? 'active' : ''
                        }`}
                        onClick={() => setTimelineMode('table')}
                      >
                        Table
                      </button>
                    </div>
                  </div>
                </div>
                {timelineMode === 'overview' && (
                  <div className='timeline-overview-layout'>
                    <div className='timeline-chart card-ish'>
                      <div className='chart-header-row'>
                        <div className='timeline-legend'>
                          <span className='legend-swatch swatch-low'></span>
                          <span className='legend-label'>Low</span>
                          <span className='legend-swatch swatch-mid'></span>
                          <span className='legend-label'>Medium</span>
                          <span className='legend-swatch swatch-high'></span>
                          <span className='legend-label'>High</span>
                        </div>
                        <div className='mini-total'>
                          <span className='mini-total-label'>Total</span>
                          <span className='mini-total-value'>
                            {totalTimelineCount}
                          </span>
                        </div>
                      </div>
                      <div className='timeline-bars enhanced'>
                        {paginatedTimeline.map((day) => {
                          const intensity = maxTimelineCount
                            ? day.count / maxTimelineCount
                            : 0;
                          const chartMax = 150;
                          const barHeight = Math.max(6, intensity * chartMax);
                          let barClass = 'low';
                          if (intensity > 0.66) barClass = 'high';
                          else if (intensity > 0.33) barClass = 'mid';
                          const tooltipContent = (
                            <div className='tt-inner'>
                              <div className='tt-date'>
                                {moment(day.date).format('MMM DD, YYYY')}
                              </div>
                              <div className='tt-total'>
                                <strong>{day.count}</strong> activities (
                                {day.pct}%)
                              </div>
                            </div>
                          );
                          const showTooltip = (evt) => {
                            const b = evt.currentTarget.getBoundingClientRect();
                            setBarTooltip({
                              visible: true,
                              x: b.left + b.width / 2,
                              y: b.top - 8,
                              content: tooltipContent,
                            });
                          };
                          return (
                            <div
                              key={day.date}
                              className='timeline-bar enhanced'
                            >
                              <div
                                className={`bar enhanced ${barClass}`}
                                style={{ height: barHeight }}
                                role='button'
                                tabIndex={0}
                                onClick={() => handleBarClick(day.date)}
                                onMouseEnter={showTooltip}
                                onMouseMove={showTooltip}
                                onMouseLeave={hideTooltip}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleBarClick(day.date);
                                  }
                                }}
                              >
                                <span className='bar-count'>{day.count}</span>
                              </div>
                              <div className='bar-label'>
                                {moment(day.date).format('MMM DD')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {barTooltip.visible && (
                        <div
                          className='timeline-tooltip'
                          style={{ left: barTooltip.x, top: barTooltip.y }}
                        >
                          {barTooltip.content}
                        </div>
                      )}
                      {totalTimelinePages > 1 && (
                        <div className='timeline-page-controls'>
                          <button
                            className='pg-btn'
                            disabled={clampedTimelinePage === 1}
                            onClick={() => setTimelinePage(1)}
                          >
                            «
                          </button>
                          <button
                            className='pg-btn'
                            disabled={clampedTimelinePage === 1}
                            onClick={() =>
                              setTimelinePage(clampedTimelinePage - 1)
                            }
                          >
                            ‹
                          </button>
                          <span className='timeline-page-summary'>
                            Page {clampedTimelinePage} of {totalTimelinePages}
                          </span>
                          <button
                            className='pg-btn'
                            disabled={
                              clampedTimelinePage === totalTimelinePages
                            }
                            onClick={() =>
                              setTimelinePage(clampedTimelinePage + 1)
                            }
                          >
                            ›
                          </button>
                          <button
                            className='pg-btn'
                            disabled={
                              clampedTimelinePage === totalTimelinePages
                            }
                            onClick={() => setTimelinePage(totalTimelinePages)}
                          >
                            »
                          </button>
                        </div>
                      )}
                    </div>
                    <div className='status-pie-wrapper card-ish'>
                      {statusPieData.length && statusTotal ? (
                        <div className='status-pie-layout merged'>
                          <div className='pie-chart-block'>
                            <svg viewBox='0 0 140 140' width='200' height='200'>
                              <g transform='translate(70,70)'>
                                {renderPieArcs(
                                  statusPieData,
                                  statusTotal,
                                  setStatusPieTooltip,
                                  hideStatusPieTooltip
                                )}
                                <text
                                  textAnchor='middle'
                                  dominantBaseline='middle'
                                  fontSize='15'
                                  fontWeight='600'
                                >
                                  {statusTotal}
                                </text>
                                <text
                                  y='18'
                                  textAnchor='middle'
                                  fontSize='10'
                                  fill='#6b7280'
                                >
                                  statuses
                                </text>
                              </g>
                            </svg>
                          </div>
                          {statusPieTooltip.visible && (
                            <div
                              className='timeline-tooltip'
                              style={{
                                left: statusPieTooltip.x,
                                top: statusPieTooltip.y,
                              }}
                            >
                              {statusPieTooltip.content}
                            </div>
                          )}
                          <div className='pie-legend-block'>
                            <h4>Status Breakdown</h4>
                            <ul className='pie-legend'>
                              {statusPieData.map((seg) => (
                                <li key={seg.name}>
                                  <span
                                    className='legend-dot'
                                    style={{ background: seg.color }}
                                  ></span>
                                  <span className='legend-name'>
                                    {seg.name}
                                  </span>
                                  <span className='legend-value'>
                                    {seg.value}
                                  </span>
                                  <span className='legend-pct'>{seg.pct}%</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <div className='empty-state compact'>
                          <i className='fa fa-info-circle'></i>
                          <h3>No Status Data</h3>
                          <p>No status breakdown available.</p>
                        </div>
                      )}
                    </div>
                    <div className='status-pie-wrapper card-ish'>
                      {processPieData.length && processTotal ? (
                        <div className='status-pie-layout merged'>
                          <div className='pie-chart-block'>
                            <svg viewBox='0 0 140 140' width='200' height='200'>
                              <g transform='translate(70,70)'>
                                {renderPieArcs(
                                  processPieData,
                                  processTotal,
                                  setProcessPieTooltip,
                                  hideProcessPieTooltip
                                )}
                                <text
                                  textAnchor='middle'
                                  dominantBaseline='middle'
                                  fontSize='15'
                                  fontWeight='600'
                                >
                                  {processTotal}
                                </text>
                                <text
                                  y='18'
                                  textAnchor='middle'
                                  fontSize='10'
                                  fill='#6b7280'
                                >
                                  processes
                                </text>
                              </g>
                            </svg>
                          </div>
                          <div className='pie-legend-block'>
                            <h4>Process Breakdown</h4>
                            <ul className='pie-legend'>
                              {processPieData.map((seg) => (
                                <li key={seg.name}>
                                  <span
                                    className='legend-dot'
                                    style={{ background: seg.color }}
                                  ></span>
                                  <span className='legend-name'>
                                    {seg.name}
                                  </span>
                                  <span className='legend-value'>
                                    {seg.value}
                                  </span>
                                  <span className='legend-pct'>{seg.pct}%</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {processPieTooltip.visible && (
                            <div
                              className='timeline-tooltip'
                              style={{
                                left: processPieTooltip.x,
                                top: processPieTooltip.y,
                              }}
                            >
                              {processPieTooltip.content}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className='empty-state compact'>
                          <i className='fa fa-info-circle'></i>
                          <h3>No Process Data</h3>
                          <p>No process breakdown available.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {timelineMode === 'table' && (
                  <div className='timeline-table-wrapper'>
                    <table className='timeline-table'>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Weekday</th>
                          <th>Activities</th>
                          <th>% of Total</th>
                          <th>Cumulative</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTimeline.map((d) => (
                          <tr
                            key={d.date}
                            onClick={() => handleBarClick(d.date)}
                            className='row-clickable'
                          >
                            <td>{moment(d.date).format('MMM DD, YYYY')}</td>
                            <td>{d.weekday}</td>
                            <td>
                              <span className='badge-count'>{d.count}</span>
                            </td>
                            <td>{d.pct}%</td>
                            <td>{d.cumulative}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={2}>Total</td>
                          <td>{totalTimelineCount}</td>
                          <td>100%</td>
                          <td>{totalTimelineCount}</td>
                        </tr>
                      </tfoot>
                    </table>
                    {totalTimelinePages > 1 && (
                      <div className='timeline-page-controls'>
                        <button
                          className='pg-btn'
                          disabled={clampedTimelinePage === 1}
                          onClick={() => setTimelinePage(1)}
                        >
                          «
                        </button>
                        <button
                          className='pg-btn'
                          disabled={clampedTimelinePage === 1}
                          onClick={() =>
                            setTimelinePage(clampedTimelinePage - 1)
                          }
                        >
                          ‹
                        </button>
                        <span className='timeline-page-summary'>
                          Page {clampedTimelinePage} of {totalTimelinePages}
                        </span>
                        <button
                          className='pg-btn'
                          disabled={clampedTimelinePage === totalTimelinePages}
                          onClick={() =>
                            setTimelinePage(clampedTimelinePage + 1)
                          }
                        >
                          ›
                        </button>
                        <button
                          className='pg-btn'
                          disabled={clampedTimelinePage === totalTimelinePages}
                          onClick={() => setTimelinePage(totalTimelinePages)}
                        >
                          »
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className='empty-state'>
                <i className='fa fa-chart-line'></i>
                <h3>No Timeline Data</h3>
                <p>Adjust date range to view activity.</p>
              </div>
            )}
          </div>
        )}

        {activeView === 'employees' && (
          <div className='employees-view'>
            <div className='team-header-row'>
              <h3 className='section-heading'>Team Performance</h3>
              <div className='team-tools'>
                <input
                  type='text'
                  placeholder='Search member...'
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  className='team-search-input'
                />
              </div>
            </div>
            {teamSorted.length ? (
              <div className='employees-table'>
                <table className='table team-performance-table improved'>
                  <colgroup>
                    <col className='col-member' />
                    <col className='col-total' />
                    <col className='col-completed' />
                    <col className='col-overdue' />
                    <col className='col-completion' />
                    <col className='col-statuses' />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className='col-member'>Member</th>
                      <th
                        className='col-total sortable numeric'
                        onClick={() => cycleSort('total_tasks')}
                      >
                        Total {sortIcon('total_tasks')}
                      </th>
                      <th
                        className='col-completed sortable numeric'
                        onClick={() => cycleSort('completed_tasks')}
                      >
                        Completed {sortIcon('completed_tasks')}
                      </th>
                      <th
                        className='col-overdue sortable numeric'
                        onClick={() => cycleSort('overdue_tasks')}
                      >
                        Overdue {sortIcon('overdue_tasks')}
                      </th>
                      <th className='col-completion'>Completion</th>
                      <th className='col-statuses'>Statuses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamSorted.map((emp, idx) => {
                      const total = emp.total_tasks || 0;
                      const completed = emp.completed_tasks || 0;
                      const overdue = emp.overdue_tasks || 0;
                      const completionPct = total
                        ? Math.round((completed / total) * 100)
                        : 0;
                      const statuses = Array.isArray(emp.statuses)
                        ? emp.statuses
                        : [];
                      return (
                        <tr
                          key={emp.employee?._id || idx}
                          className='team-row static'
                        >
                          <td className='col-member'>
                            <div className='employee-info'>
                              <strong className='emp-name'>
                                {emp.employee?.name}
                              </strong>
                              <div className='employee-email'>
                                {emp.employee?.email}
                              </div>
                            </div>
                          </td>
                          <td className='numeric col-total'>{total}</td>
                          <td className='numeric col-completed'>{completed}</td>
                          <td className='numeric overdue-cell col-overdue'>
                            {overdue}
                          </td>
                          <td className='col-completion'>
                            <div className='completion-bar-wrapper subtle'>
                              <div className='completion-bar'>
                                <span
                                  style={{ width: `${completionPct}%` }}
                                ></span>
                              </div>
                              <span className='completion-label'>
                                {completionPct}%
                              </span>
                            </div>
                          </td>
                          <td className='col-statuses'>
                            {statuses.length ? (
                              <ul className='status-inline-chips'>
                                {statuses.map((s) => (
                                  <li
                                    key={s.status_id || s.name}
                                    className='chip-status'
                                  >
                                    <span
                                      className='dot'
                                      style={{
                                        background: s.color || '#6b7280',
                                      }}
                                    ></span>
                                    <span className='label'>{s.name}</span>
                                    <span className='count'>{s.count}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className='status-empty'>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='empty-state'>
                <i className='fa fa-users'></i>
                <h3>No Team Data</h3>
                <p>No team members found for filters/search.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Metadata */}
      {/* {dashboardData?.metadata && (
        <div className='dashboard-metadata'>
          <small>
            Generated at: {formatDateTime(dashboardData.metadata.generated_at)}{' '}
            | Performance tip: {dashboardData.metadata.performance_tip}
          </small>
        </div>
      )} */}
    </div>
  );
};

export default ClientDashboardView;
